import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatTL, formatDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ProductImage from "../components/ProductImage";
import { toast } from "sonner";
import { ArrowLeft, XCircle, Undo2 } from "lucide-react";

const STATUS_META = {
  completed: { label: "Tamamlandı", class: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  cancelled: { label: "İptal", class: "bg-red-100 text-red-800 border-red-200" },
  refunded: { label: "İade", class: "bg-amber-100 text-amber-800 border-amber-200" },
  partial_refunded: { label: "Kısmi İade", class: "bg-amber-100 text-amber-800 border-amber-200" },
};

export default function SaleDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { isAdmin } = useAuth();
  const [sale, setSale] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundItems, setRefundItems] = useState({}); // pid -> qty
  const [refundReason, setRefundReason] = useState("");
  const [refundMethod, setRefundMethod] = useState("nakit");
  const [restock, setRestock] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/sales/${id}`).then((r) => setSale(r.data));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!sale) {
    return <div className="text-stone-500">Yükleniyor…</div>;
  }

  const st = STATUS_META[sale.status] || STATUS_META.completed;

  const doCancel = async () => {
    if (!cancelReason.trim()) return toast.error("Neden zorunlu");
    setBusy(true);
    try {
      await api.post(`/sales/${id}/cancel`, { reason: cancelReason });
      toast.success("Satış iptal edildi");
      setCancelOpen(false);
      setCancelReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız");
    } finally {
      setBusy(false);
    }
  };

  const doRefund = async () => {
    if (!refundReason.trim()) return toast.error("Neden zorunlu");
    const items = Object.entries(refundItems)
      .map(([pid, q]) => ({ product_id: pid, quantity: Number(q) }))
      .filter((it) => it.quantity > 0);
    if (!items.length) return toast.error("İade edilecek ürün seçin");
    setBusy(true);
    try {
      await api.post(`/sales/${id}/refund`, {
        items,
        reason: refundReason,
        refund_method: refundMethod,
        restock,
      });
      toast.success("İade tamamlandı");
      setRefundOpen(false);
      setRefundReason("");
      setRefundItems({});
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İade başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="sale-detail-root" className="max-w-4xl">
      <button
        onClick={() => nav(-1)}
        className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-800 mb-3"
      >
        <ArrowLeft className="w-4 h-4" /> Geri
      </button>

      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="font-mono-code text-[10px] tracking-[0.3em] text-stone-500">
            SATIŞ DETAYI
          </div>
          <h1 className="font-serif-display text-3xl text-stone-900">
            {sale.sale_number}
          </h1>
          <div className="text-sm text-stone-500 mt-1 font-mono-code">
            {formatDate(sale.created_at)} · Kasiyer: {sale.cashier_name}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex text-xs px-3 py-1 rounded-full border font-medium ${st.class}`}>
            {st.label}
          </span>
          {isAdmin && sale.status !== "cancelled" && (
            <>
              <button
                data-testid="sale-refund-btn"
                onClick={() => setRefundOpen(true)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-amber-300 text-amber-800 text-xs hover:bg-amber-50"
              >
                <Undo2 className="w-3.5 h-3.5" /> İade Al
              </button>
              <button
                data-testid="sale-cancel-btn"
                onClick={() => setCancelOpen(true)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-300 text-red-700 text-xs hover:bg-red-50"
              >
                <XCircle className="w-3.5 h-3.5" /> İptal Et
              </button>
            </>
          )}
        </div>
      </div>

      <div className="card-lux overflow-hidden mb-4">
        <table className="min-w-full">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr className="text-xs uppercase tracking-wider text-stone-500">
              <th className="px-3 py-3 text-left">Ürün</th>
              <th className="px-3 py-3 text-left">SKU / Barkod</th>
              <th className="px-3 py-3 text-right">Adet</th>
              <th className="px-3 py-3 text-right">Liste</th>
              <th className="px-3 py-3 text-right">İndirim</th>
              <th className="px-3 py-3 text-right">Birim</th>
              <th className="px-3 py-3 text-right">Toplam</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {sale.items.map((l) => (
              <tr key={l.product_id}>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-3">
                    <ProductImage path={l.image_path} size={40} alt={l.name} />
                    <div>
                      <div className="text-sm font-medium text-stone-900">{l.name}</div>
                      <div className="text-xs text-stone-500">{l.feature}</div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3">
                  <div className="text-xs font-mono-code text-stone-600">{l.sku}</div>
                  <div className="text-[11px] font-mono-code text-stone-400">{l.barcode}</div>
                </td>
                <td className="px-3 py-3 text-right font-mono-code text-sm">
                  {l.quantity}
                  {l.refunded_qty ? <span className="text-amber-700"> (-{l.refunded_qty})</span> : null}
                </td>
                <td className="px-3 py-3 text-right font-mono-code text-sm">{formatTL(l.list_price)}</td>
                <td className="px-3 py-3 text-right font-mono-code text-sm text-red-700">-{formatTL(l.line_discount)}</td>
                <td className="px-3 py-3 text-right font-mono-code text-sm">{formatTL(l.unit_price)}</td>
                <td className="px-3 py-3 text-right font-mono-code text-sm font-semibold">{formatTL(l.line_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card-lux p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Info label="Ara Toplam" value={formatTL(sale.subtotal)} />
        <Info label="İndirim" value={`-${formatTL(sale.discount_total)}`} />
        <Info label="Genel Toplam" value={<span className="font-serif-display text-2xl text-amber-800">{formatTL(sale.total)}</span>} />
        <Info label="Ödeme" value={sale.payment_method === "karma" ? `Karma — Nakit ${formatTL(sale.cash_amount)}, Havale ${formatTL(sale.transfer_amount)}` : (sale.payment_method || "").toUpperCase()} />
        {sale.status === "cancelled" && (
          <Info label="İptal Nedeni" value={`${sale.cancel_reason || "-"} · ${sale.cancelled_by_name || ""} · ${formatDate(sale.cancelled_at)}`} />
        )}
      </div>

      {sale.refunds && sale.refunds.length > 0 && (
        <div className="card-lux p-5 mt-4">
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-3">İade Kayıtları</div>
          <div className="space-y-2">
            {sale.refunds.map((r) => (
              <div key={r.id} className="text-sm">
                <span className="font-mono-code text-amber-800">-{formatTL(r.total)}</span> · {r.refund_method?.toUpperCase()} · {r.reason} · {r.created_by_name} · <span className="text-stone-500 font-mono-code text-xs">{formatDate(r.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {cancelOpen && (
        <Modal title="Satışı İptal Et" onClose={() => setCancelOpen(false)}>
          <p className="text-sm text-stone-500 mb-3">
            İptal edildiğinde tüm ürünler stoğa geri eklenir. Bu işlem geri alınamaz.
          </p>
          <textarea
            data-testid="cancel-reason"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            className="w-full px-3 py-2 border border-stone-200 rounded-lg outline-none focus:border-amber-400 mb-4"
            rows={3}
            placeholder="İptal nedeni"
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => setCancelOpen(false)} className="px-4 py-2 rounded-lg border border-stone-200 text-sm">
              Vazgeç
            </button>
            <button data-testid="cancel-confirm" onClick={doCancel} disabled={busy} className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm disabled:opacity-60">
              {busy ? "Kaydediliyor…" : "İptal Et"}
            </button>
          </div>
        </Modal>
      )}

      {refundOpen && (
        <Modal title="İade Al" onClose={() => setRefundOpen(false)}>
          <div className="space-y-2 mb-4 max-h-[40vh] overflow-y-auto">
            {sale.items.map((l) => {
              const maxQty = l.quantity - (l.refunded_qty || 0);
              return (
                <div key={l.product_id} className="flex items-center gap-3 border border-stone-200 rounded-lg px-3 py-2">
                  <ProductImage path={l.image_path} size={40} alt={l.name} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{l.name}</div>
                    <div className="text-xs text-stone-500">Max {maxQty} · {formatTL(l.unit_price)}</div>
                  </div>
                  <input
                    data-testid={`refund-qty-${l.sku}`}
                    type="number"
                    min="0"
                    max={maxQty}
                    disabled={maxQty <= 0}
                    value={refundItems[l.product_id] || ""}
                    onChange={(e) => setRefundItems((prev) => ({ ...prev, [l.product_id]: e.target.value }))}
                    className="w-20 px-2 py-1 border border-stone-200 rounded-md text-sm text-right font-mono-code"
                    placeholder="0"
                  />
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <label className="block">
              <div className="text-[10px] uppercase text-stone-500 mb-1">İade Yöntemi</div>
              <select
                value={refundMethod}
                onChange={(e) => setRefundMethod(e.target.value)}
                className="w-full px-2 py-2 border border-stone-200 rounded-lg bg-white text-sm"
              >
                <option value="nakit">Nakit</option>
                <option value="havale">Havale</option>
              </select>
            </label>
            <label className="block">
              <div className="text-[10px] uppercase text-stone-500 mb-1">Stoğa Ekle</div>
              <select
                value={restock ? "yes" : "no"}
                onChange={(e) => setRestock(e.target.value === "yes")}
                className="w-full px-2 py-2 border border-stone-200 rounded-lg bg-white text-sm"
              >
                <option value="yes">Evet</option>
                <option value="no">Hayır (hasarlı)</option>
              </select>
            </label>
          </div>
          <textarea
            data-testid="refund-reason"
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            className="w-full px-3 py-2 border border-stone-200 rounded-lg outline-none focus:border-amber-400 mb-4"
            rows={2}
            placeholder="İade nedeni"
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => setRefundOpen(false)} className="px-4 py-2 rounded-lg border border-stone-200 text-sm">
              Vazgeç
            </button>
            <button data-testid="refund-confirm" onClick={doRefund} disabled={busy} className="px-4 py-2 rounded-lg gold-gradient text-white text-sm disabled:opacity-60">
              {busy ? "Kaydediliyor…" : "İade Yap"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className="mt-1 text-stone-900">{value}</div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md border border-stone-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-serif-display text-2xl">{title}</div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-stone-100">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
